<template>
  <div class="modal fade" id="modal-batch-nf" role="dialog" tabindex="-1" data-backdrop="static" data-keyboard="false">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <button v-if="!running" type="button" class="close" data-dismiss="modal" aria-label="Fechar" @click="finish">
            <span aria-hidden="true">&times;</span>
          </button>
          <h4 class="modal-title">Emitir notas fiscais em lote</h4>
        </div>

        <div class="modal-body">
          <div class="batch-options" v-if="!started">
            <div class="row">
              <div class="col-sm-4">
                <label>Tipo de cobrança</label>
                <select v-model="billingType" class="form-control">
                  <option :value="0">MAM</option>
                  <option :value="1">BOH</option>
                </select>
              </div>
              <div class="col-sm-4">
                <label>Data de emissão</label>
                <input v-model="emissionDate" type="date" class="form-control">
              </div>
              <div class="col-sm-4 batch-total">
                <span class="batch-total-label">{{ rows.length }} faturas</span>
                <strong>{{ totalValue | currency('R$', 2, {decimalSeparator: ','}) }}</strong>
              </div>
            </div>
          </div>

          <div class="batch-progress" v-if="started">
            <div class="progress">
              <div class="progress-bar progress-bar-success" :style="{width: pct(okCount) + '%'}"></div>
              <div class="progress-bar progress-bar-danger" :style="{width: pct(errorCount) + '%'}"></div>
            </div>
            <p class="batch-summary">
              <span class="ok"><i class="fa fa-check-circle"></i> {{ okCount }} emitidas</span>
              <span class="err" v-if="errorCount"><i class="fa fa-exclamation-circle"></i> {{ errorCount }} com erro</span>
              <span class="pend" v-if="pendingCount">{{ pendingCount }} restantes</span>
            </p>
          </div>

          <div class="table-scrollable batch-table">
            <table class="table table-hover table-light">
              <thead>
              <tr>
                <th>Empresa</th>
                <th class="text-right">Valor</th>
                <th>Situação</th>
                <th v-if="!started"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="r in rows" :key="r.id" :class="'row-' + r.status">
                <td>{{ r.companyName }}<small class="text-muted batch-ref"> {{ r.paymentStatusStr }}</small></td>
                <td class="text-right">{{ r.value | currency('R$', 2, {decimalSeparator: ','}) }}</td>
                <td>
                  <span v-if="r.status === 'idle'" class="text-muted">{{ started ? 'Na fila' : 'Pronta' }}</span>
                  <span v-else-if="r.status === 'running'" class="text-info"><i class="fa fa-spinner fa-spin"></i> {{ r.step }}</span>
                  <span v-else-if="r.status === 'ok'" class="text-success"><i class="fa fa-check"></i> {{ r.message }}</span>
                  <span v-else class="text-danger"><i class="fa fa-times"></i> {{ r.message }}</span>
                </td>
                <td v-if="!started" class="text-right">
                  <a class="batch-remove" title="Tirar do lote" @click="remove(r)"><i class="fa fa-times"></i></a>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="modal-footer">
          <template v-if="!started">
            <button type="button" class="btn btn-default" data-dismiss="modal" @click="finish">Voltar</button>
            <button type="button" class="btn btn-primary" :disabled="!rows.length" @click="start">
              Emitir {{ rows.length }} {{ rows.length === 1 ? 'nota' : 'notas' }}
            </button>
          </template>
          <template v-else>
            <button v-if="running" type="button" class="btn btn-default" :disabled="stopping" @click="stop">
              {{ stopping ? 'Pausando...' : 'Pausar' }}
            </button>
            <button v-if="!running && retryable" type="button" class="btn btn-warning" @click="run(true)">
              Tentar novamente ({{ retryable }})
            </button>
            <button v-if="!running" type="button" class="btn btn-primary" data-dismiss="modal" @click="finish">Concluir</button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { REQUEST_SERVICE } from "../../../../../requestConfig";
import { CONTEXT_COMPANY, COMPANY_TO_REGISTER_INFOS, INVOICE_CREATE } from "../../../../../urlConfig";

const TO_REGISTER_TOKEN = "83ff3cb222es847b485251d0d86DDe63f5";
const CONCURRENCY = 3;

const REQUIRED = [
  ["cnpj", "CNPJ/CPF"],
  ["name", "razão social"],
  ["email", "e-mail fiscal"],
  ["zipCode", "CEP"],
  ["number", "número"],
  ["street", "logradouro"],
  ["city", "cidade"],
  ["state", "estado"],
  ["district", "bairro"],
  ["phone", "telefone"],
];

const digits = (v) => (v || "").toString().replace(/\D/g, "");

const apiError = (err, fallback) => {
  const d = err && err.response && err.response.data;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  return d.message || (d.errorDetails && d.errorDetails.join && d.errorDetails.join(" ")) ||
    (d.erros && d.erros[0] && d.erros[0].value) || fallback;
};

export default {
  name: "batch-invoice-modal",
  props: {
    items: { type: Array, default: () => [] },
  },
  data() {
    return {
      rows: [],
      billingType: 0,
      emissionDate: moment().format("YYYY-MM-DD"),
      started: false,
      running: false,
      stopping: false,
      companyCache: {},
    };
  },
  computed: {
    totalValue() {
      return this.rows.reduce((s, r) => s + (r.value || 0), 0);
    },
    okCount() {
      return this.rows.filter((r) => r.status === "ok").length;
    },
    errorCount() {
      return this.rows.filter((r) => r.status === "error").length;
    },
    pendingCount() {
      return this.rows.filter((r) => r.status === "idle" || r.status === "running").length;
    },
    retryable() {
      return this.errorCount + this.rows.filter((r) => r.status === "idle").length;
    },
  },
  methods: {
    open() {
      this.rows = this.items.map((p) => ({
        id: p.id,
        companyId: p.companyId,
        companyName: p.companyName,
        paymentStatusStr: p.paymentStatusStr,
        value: Number(p.totalCost) || 0,
        status: "idle",
        step: "",
        message: "",
      }));
      this.started = false;
      this.running = false;
      this.stopping = false;
      this.companyCache = {};
      this.$nextTick(() => $("#modal-batch-nf").modal("show"));
    },
    finish() {
      this.$emit("finished", this.rows.filter((r) => r.status === "ok").map((r) => r.id));
    },
    remove(row) {
      this.rows = this.rows.filter((r) => r.id !== row.id);
      this.$emit("removed", row.id);
    },
    pct(n) {
      return this.rows.length ? (n * 100) / this.rows.length : 0;
    },
    start() {
      if (!this.rows.length) return;
      this.started = true;
      this.run(false);
    },
    stop() {
      this.stopping = true;
    },
    async run(onlyFailed) {
      if (onlyFailed) {
        this.rows.filter((r) => r.status === "error").forEach((r) => {
          r.status = "idle";
          r.message = "";
        });
      }
      this.running = true;
      this.stopping = false;
      const queue = this.rows.filter((r) => r.status === "idle");
      const worker = async () => {
        while (queue.length && !this.stopping) {
          await this.emit(queue.shift());
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
      this.running = false;
      this.stopping = false;
    },
    loadCompany(companyId) {
      if (!this.companyCache[companyId]) {
        this.companyCache[companyId] = REQUEST_SERVICE.get(CONTEXT_COMPANY + companyId)
          .then((ctx) => {
            const url = `${COMPANY_TO_REGISTER_INFOS}?c=${encodeURIComponent(companyId)}&t=${TO_REGISTER_TOKEN}`;
            return REQUEST_SERVICE.requestContext("GET", url, ctx.data);
          })
          .then((res) => this.toFiscalData(res.data, companyId));
      }
      return this.companyCache[companyId];
    },
    toFiscalData(data, companyId) {
      const d = data || {};
      const a = d.address || d.Address || {};
      const b = d.bussinessInfo || {};
      const p = d.phone || d.Phone || {};
      return {
        cnpj: digits(b.cnpj || d.cnpj),
        name: (d.representationName || d.name || d.razaoSocial || "").trim(),
        email: (d.financialEmail || d.email || "").trim(),
        zipCode: digits(a.zipCode || d.zipCode),
        number: (a.number || d.number || "").toString().trim(),
        street: (a.street || d.street || "").trim(),
        city: (a.city || d.city || "").trim(),
        state: (a.state || d.state || "").toString().trim().substring(0, 2),
        district: (a.neighborhood || a.district || d.district || "").trim(),
        phone: digits(p.phoneNumber || p.number || d.phone),
        complement: (a.complement || d.complement || "").trim(),
        companyId,
      };
    },
    async emit(row) {
      row.status = "running";
      row.step = "Buscando dados fiscais";
      try {
        const fiscal = await this.loadCompany(row.companyId);
        const missing = REQUIRED.filter(([k]) => !fiscal[k]).map(([, label]) => label);
        if (missing.length) {
          row.status = "error";
          row.message = "Cadastro fiscal incompleto: " + missing.join(", ");
          return;
        }
        if (row.value <= 0) {
          row.status = "error";
          row.message = "Valor da fatura zerado";
          return;
        }
        row.step = "Emitindo";
        const payload = Object.assign({}, fiscal, {
          billingType: Number(this.billingType),
          emissionDate: moment(this.emissionDate).format("YYYY-MM-DDTHH:mm:ss"),
          value: row.value,
          notes: "",
          referenceId: row.id,
        });
        const res = await REQUEST_SERVICE.post(INVOICE_CREATE, payload);
        row.status = "ok";
        row.message = (res.data && res.data.message) || "NF emitida";
      } catch (err) {
        row.status = "error";
        row.message = apiError(err, "Falha ao emitir NF");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.batch-options {
  margin-bottom: 16px;
}
.batch-total {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: right;
  strong {
    font-size: 22px;
  }
}
.batch-total-label {
  color: #888;
  font-size: 12px;
}
.batch-progress .progress {
  height: 10px;
  margin-bottom: 8px;
}
.batch-summary {
  margin-bottom: 12px;
  span {
    margin-right: 16px;
  }
  .ok { color: #3c763d; }
  .err { color: #a94442; }
  .pend { color: #888; }
}
.batch-table {
  max-height: 340px;
  overflow-y: auto;
}
.row-error td {
  background: #fdf3f2;
}
.row-ok td {
  background: #f3faf3;
}
.batch-ref {
  margin-left: 6px;
}
.batch-remove {
  color: #999;
  cursor: pointer;
  &:hover { color: #a94442; }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/payment/listPayment/batchInvoiceModal.vue